import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useB2BAuth } from '../context/B2BContext';
import { LogOut, Activity, ExternalLink, Copy, CheckCircle2, Clock, AlertCircle, RefreshCw, Download, FileText, X, Layers, Shield } from 'lucide-react';
import config from '../config';
import { b2bApi, b2bTelecharger, messageExportPartiel, URL_CONNEXION_B2B } from '../services/b2bApi';
import { creerRafraichissement } from '../services/rafraichissement';

export default function B2BDashboard() {
  const { logout } = useB2BAuth();
  const navigate = useNavigate();
  
  const [usage, setUsage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL'); // ALL, PENDING, CONFIRMED, FAILED
  const [copiedId, setCopiedId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timelineProofId, setTimelineProofId] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [inclusionProof, setInclusionProof] = useState(null);
  const [inclusionLoading, setInclusionLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exportPartiel, setExportPartiel] = useState(null);
  const rafraichissement = useRef(null);

  useEffect(() => {
    // Un seul chargement à la fois, Retry-After respecté ; sous StrictMode, le
    // premier montage est démonté avant son chargement initial (différé).
    const r = creerRafraichissement(fetchData, { intervalleMs: 10000 });
    rafraichissement.current = r;
    r.demarrer();
    return () => r.arreter();
  }, []);

  const fetchData = async () => {
    setIsRefreshing(true);

    const [usageRes, proofsRes, summaryRes] = await Promise.all([
      b2bApi('/usage'),
      b2bApi('/proofs'),
      b2bApi('/metrics/summary')
    ]);

    // Session absente, expirée ou invalidée : reconnexion par le portail.
    if ([usageRes, proofsRes, summaryRes].some((r) => r.status === 401)) {
      rafraichissement.current?.arreter();
      navigate(URL_CONNEXION_B2B);
      return { retryAfterMs: null };
    }

    if (usageRes.ok) setUsage(usageRes.data);

    if (proofsRes.ok) setProofs(proofsRes.data.proofs || []);

    if (summaryRes.ok) setSummary(summaryRes.data);

    // Premier échec rencontré, affiché tel quel ; effacé dès que tout répond.
    const echec = [usageRes, proofsRes, summaryRes].find((r) => !r.ok);
    setLoadError(echec ? echec.error : null);

    if (!echec) setLastUpdated(new Date());
    setLoading(false);
    setIsRefreshing(false);

    // Délai le plus long imposé par le serveur (429 Retry-After), pour le planificateur.
    const delais = [usageRes, proofsRes, summaryRes].map((r) => r.retryAfterMs).filter((d) => d > 0);
    return { retryAfterMs: delais.length ? Math.max(...delais) : null };
  };

  const fetchTimeline = async (proofId) => {
    setTimelineProofId(proofId);
    setTimelineLoading(true);
    const res = await b2bApi(`/proofs/${proofId}/timeline`);
    if (res.ok) setTimelineData(res.data.timeline || []);
    else console.error(res.error);
    setTimelineLoading(false);

    // Also try to fetch inclusion proof
    setInclusionLoading(true);
    setInclusionProof(null);
    const incRes = await b2bApi(`/proofs/${proofId}/inclusion-proof`);
    if (incRes.ok) setInclusionProof(incRes.data);
    else console.error(incRes.error);
    setInclusionLoading(false);
  };

  // Sous session portail, fichier reçu puis enregistré localement : aucune clé dans le navigateur.
  const handleExportCsv = async () => {
    setExportError(null);
    setExportPartiel(null);
    const nom = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
    const res = await b2bTelecharger('/audit/export.csv', nom);
    if (!res.ok) setExportError(res.error);
    else setExportPartiel(messageExportPartiel(res.export));
  };

  const handleLogout = async () => {
    rafraichissement.current?.arreter();
    await logout();
    navigate(URL_CONNEXION_B2B);
  };

  const copyToClipboard = (e, text, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getAnchorStatus = (proof) => {
    if (!proof.anchors || proof.anchors.length === 0) return 'PENDING';
    return proof.anchors[0].status;
  };

  const filteredProofs = proofs.filter(p => {
    if (filter === 'ALL') return true;
    return getAnchorStatus(p) === filter;
  });

  if (loading && !usage) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const usagePercent = usage?.quota_day === "unlimited" ? 0 : Math.min(100, (usage?.calls_today / usage?.quota_day) * 100);

  return (
    <div className="min-h-screen bg-[#0b1120] text-gray-100 font-sans p-6">
      <div className="max-w-7xl mx-auto">
        {exportError && (
          <div role="alert" className="mb-6 p-3 rounded-lg bg-red-900/50 border border-red-500/50 text-red-200 text-sm">
            Export CSV : {exportError}
          </div>
        )}

        {exportPartiel && (
          <div role="status" className="mb-6 p-3 rounded-lg bg-amber-900/40 border border-amber-500/50 text-amber-100 text-sm">
            {exportPartiel}
          </div>
        )}

        {loadError && (
          <div role="alert" className="mb-6 p-3 rounded-lg bg-red-900/50 border border-red-500/50 text-red-200 text-sm">
            {loadError}
          </div>
        )}

        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-center mb-8 bg-[#162032] p-4 rounded-xl border border-gray-800">
          <div className="flex items-center gap-3 mb-4 md:mb-0">
            <div className="w-10 h-10 bg-teal-900/30 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-teal-50">
                Tableau de Bord Partenaire
              </h1>
              <p className="text-sm text-gray-400">Plan: {usage?.plan?.toUpperCase()}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              {lastUpdated && <span>Dernière mise à jour : {lastUpdated.toLocaleTimeString()}</span>}
              <button 
                onClick={() => rafraichissement.current?.rafraichirMaintenant()} 
                disabled={isRefreshing}
                className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
                title="Rafraîchir maintenant"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <button 
              onClick={() => navigate('/b2b/merkle')}
              className="flex items-center gap-2 px-4 py-2 text-sm text-teal-400 bg-teal-900/30 hover:bg-teal-900/50 rounded-lg transition-colors border border-teal-800/50"
            >
              <Layers className="w-4 h-4" />
              Merkle Batches
            </button>
            <button 
              onClick={handleExportCsv}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors border border-gray-700"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors border border-gray-700"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {/* Quota Gauge */}
          <div className="bg-[#162032] p-6 rounded-xl border border-gray-800 flex flex-col justify-center">
            <h2 className="text-sm font-medium text-gray-400 mb-4 uppercase tracking-wider">Consommation</h2>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block text-teal-400">
                    Aujourd'hui
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold inline-block text-gray-300">
                    {usage?.calls_today} / {usage?.quota_day}
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded-full bg-gray-800 border border-gray-700">
                <div style={{ width: `${usagePercent}%` }} className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${usagePercent > 90 ? 'bg-red-500' : 'bg-teal-500'} transition-all duration-500`}></div>
              </div>
            </div>
          </div>
          
          {/* KPIs */}
          <div className="bg-[#162032] p-6 rounded-xl border border-gray-800">
             <h2 className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">Preuves (30J)</h2>
             <div className="text-3xl font-bold text-gray-100">{summary?.proofs_last_30d || 0}</div>
             <div className="text-xs text-gray-500 mt-2">Générées avec succès</div>
          </div>
          
          <div className="bg-[#162032] p-6 rounded-xl border border-gray-800">
             <h2 className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">Ancrages Sentinel</h2>
             <div className="text-3xl font-bold text-teal-400">{summary?.confirmed_count || 0}</div>
             <div className="text-xs text-gray-500 mt-2">Confirmés on-chain</div>
          </div>
          
          <div className="bg-[#162032] p-6 rounded-xl border border-gray-800">
             <h2 className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">En file d'attente</h2>
             <div className="text-3xl font-bold text-[#d4b96a]">{summary?.pending_count || 0}</div>
             <div className="text-xs text-gray-500 mt-2">En cours d'ancrage...</div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-teal-900/10 p-4 rounded-xl border border-teal-500/20 mb-8 flex items-start gap-4">
          <Shield className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-teal-100/70 leading-relaxed">
            <strong>Garantie d'Ancrage Blockchain :</strong> Toutes les preuves générées via l'API B2B sont protégées par une double garantie :
            une signature électronique immédiate (V1) suivie d'un ancrage blockchain asynchrone sur Base Sepolia (V2). Cliquez sur une ligne pour visualiser sa chronologie détaillée (Audit Trail).
          </p>
        </div>

        {/* Proofs List */}
        <div className="bg-[#162032] rounded-xl border border-gray-800 overflow-hidden flex flex-col h-[600px]">
          <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-[#1a2639] z-10">
            <h2 className="text-lg font-medium text-gray-200">Journal d'Audit (Livre de compte)</h2>
            <div className="flex gap-2 bg-[#0b1120] p-1 rounded-lg border border-gray-800">
              {['ALL', 'PENDING', 'CONFIRMED', 'FAILED'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    filter === f 
                      ? 'bg-teal-600/90 text-white shadow-sm' 
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          
          <div className="overflow-auto flex-1 p-0 relative">
            <table className="w-full text-left border-collapse">
              <thead className="bg-[#0b1120]/80 text-xs uppercase text-gray-500 sticky top-0 backdrop-blur-sm z-10 border-b border-gray-800">
                <tr>
                  <th className="px-6 py-4 font-medium">Date de création</th>
                  <th className="px-6 py-4 font-medium">Payload Hash</th>
                  <th className="px-6 py-4 font-medium">Statut Sentinel</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {filteredProofs.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                      Aucune preuve générée pour le moment avec cette clé.
                    </td>
                  </tr>
                ) : (
                  filteredProofs.map((proof) => {
                    const status = getAnchorStatus(proof);
                    
                    return (
                      <tr 
                        key={proof.id} 
                        onClick={() => fetchTimeline(proof.id)}
                        className="hover:bg-[#1c293f] transition-colors group cursor-pointer"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {new Date(proof.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-mono text-gray-400">
                          <div className="flex items-center gap-2" onClick={(e) => copyToClipboard(e, proof.payload_hash, proof.id + 'hash')}>
                            <span className="group-hover:text-gray-300 transition-colors">
                              {proof.payload_hash.substring(0, 8)}...{proof.payload_hash.slice(-6)}
                            </span>
                            <button 
                              className="text-gray-600 hover:text-teal-400 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                              title="Copier le hash"
                            >
                              {copiedId === proof.id + 'hash' ? <CheckCircle2 className="w-3.5 h-3.5 text-teal-400" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {status === 'CONFIRMED' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-[#1d3329] text-[#71c998] border border-[#2b4a3c]">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Confirmé
                            </span>
                          )}
                          {status === 'PENDING' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-[#3b341f] text-[#d4b96a] border border-[#544a2c]">
                              <Clock className="w-3.5 h-3.5" /> En attente
                            </span>
                          )}
                          {status === 'FAILED' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-[#3d2020] text-[#c96a6a] border border-[#5c2d2d]">
                              <AlertCircle className="w-3.5 h-3.5" /> Échoué
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="flex items-center justify-end gap-2">
                             <button 
                               onClick={(e) => { e.stopPropagation(); window.open(`${config.API_URL}/proof/${proof.id}`, '_blank'); }}
                               className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0b1120] hover:bg-[#131d2e] border border-gray-700 text-gray-300 rounded-md transition-colors text-xs"
                             >
                               API
                             </button>
                             <button 
                               className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-900/30 text-teal-400 border border-teal-800/50 hover:bg-teal-900/50 rounded-md transition-colors text-xs"
                             >
                               <FileText className="w-3.5 h-3.5" /> Chronologie
                             </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Timeline Modal */}
        {timelineProofId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-[#162032] border border-gray-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
              <div className="flex justify-between items-center p-5 border-b border-gray-800">
                <div>
                  <h3 className="text-lg font-bold text-gray-100">Chronologie de la preuve</h3>
                  <p className="text-xs text-gray-400 font-mono mt-1">ID: {timelineProofId}</p>
                </div>
                <button onClick={() => setTimelineProofId(null)} className="text-gray-500 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto bg-[#0b1120]">
                {timelineLoading ? (
                  <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div></div>
                ) : (
                  <div className="flex flex-col gap-8">
                    {/* Inclusion Proof Section */}
                    {inclusionProof && (
                      <div className="bg-[#162032] border border-teal-500/30 rounded-xl overflow-hidden">
                        <div className="bg-teal-900/20 px-4 py-3 border-b border-teal-500/30 flex justify-between items-center">
                          <h4 className="text-sm font-bold text-teal-400 flex items-center gap-2">
                            <Layers className="w-4 h-4" />
                            Preuve d'inclusion (Merkle Batching)
                          </h4>
                          <span className="text-xs bg-teal-900/50 text-teal-300 px-2 py-0.5 rounded border border-teal-800">
                            {inclusionProof.anchor_scheme}
                          </span>
                        </div>
                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="block text-gray-500 mb-1 text-xs uppercase tracking-wider">Batch ID</span>
                            <span className="text-gray-200">#{inclusionProof.batch_id}</span>
                          </div>
                          <div>
                            <span className="block text-gray-500 mb-1 text-xs uppercase tracking-wider">Transaction On-Chain</span>
                            {inclusionProof.tx_hash ? (
                              <a href={`https://sepolia.basescan.org/tx/${inclusionProof.tx_hash}`} target="_blank" rel="noreferrer" className="text-teal-400 hover:underline flex items-center gap-1">
                                {inclusionProof.tx_hash.substring(0, 16)}... <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="text-yellow-500">En cours...</span>
                            )}
                          </div>
                          <div className="md:col-span-2">
                            <span className="block text-gray-500 mb-1 text-xs uppercase tracking-wider">Merkle Root</span>
                            <div className="font-mono text-gray-400 break-all bg-[#0b1120] p-2 rounded border border-gray-800 text-xs">
                              {inclusionProof.merkle_root}
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <div className="flex justify-between items-end mb-1">
                              <span className="block text-gray-500 text-xs uppercase tracking-wider">Merkle Proof Path (Validation Hors-Ligne)</span>
                              <button 
                                onClick={(e) => copyToClipboard(e, JSON.stringify(inclusionProof.merkle_proof_path), 'proofPath')}
                                className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1"
                              >
                                {copiedId === 'proofPath' ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                Copier JSON
                              </button>
                            </div>
                            <div className="font-mono text-gray-400 bg-[#0b1120] p-3 rounded border border-gray-800 text-xs max-h-32 overflow-y-auto">
                              <pre>{JSON.stringify(inclusionProof.merkle_proof_path, null, 2)}</pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Timeline Section */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-200 mb-4 px-2 uppercase tracking-wider">Journal des Événements</h4>
                      <div className="space-y-6">
                        {timelineData && timelineData.length > 0 ? (
                          timelineData.map((event, idx) => (
                            <div key={idx} className="relative pl-6 border-l-2 border-gray-800 last:border-transparent">
                               <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-[#0b1120] ${
                                 event.event_type === 'PROOF_CREATED' ? 'bg-blue-500' :
                                 event.event_type.includes('QUEUED') || event.event_type === 'BATCH_ASSIGNED' ? 'bg-yellow-500' :
                                 event.event_type.includes('CONFIRMED') ? 'bg-green-500' :
                                 event.event_type.includes('FAILED') ? 'bg-red-500' : 'bg-gray-500'
                               }`}></div>
                               
                               <div className="mb-1 flex items-center gap-2">
                                 <span className="text-sm font-bold text-gray-200">{event.event_type.replace(/_/g, ' ')}</span>
                                 <span className="text-xs text-gray-500">{new Date(event.created_at).toLocaleString()}</span>
                               </div>
                               
                               <div className="text-sm text-gray-400 bg-[#162032] p-3 rounded-lg border border-gray-800/50 mt-2">
                                 <p>{event.message}</p>
                                 {event.tx_hash && (
                                   <div className="mt-2 pt-2 border-t border-gray-700/50 flex flex-col gap-1">
                                     <div className="text-xs font-mono">
                                       <span className="text-gray-500">Tx: </span>
                                       <a href={`https://sepolia.basescan.org/tx/${event.tx_hash}`} target="_blank" rel="noreferrer" className="text-teal-500 hover:underline">
                                         {event.tx_hash}
                                       </a>
                                     </div>
                                     {event.block_number && (
                                       <div className="text-xs font-mono">
                                         <span className="text-gray-500">Bloc: </span>
                                         <span className="text-gray-300">{event.block_number}</span>
                                       </div>
                                     )}
                                   </div>
                                 )}
                               </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-center">Aucun événement trouvé.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}


